import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  Paper,
  Card,
  CardContent,
  Grid,
  DialogTitle,
  Dialog,
  Button,
  Snackbar,
  Alert,
  GlobalStyles,
  Fab,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../components/Theme";
import { useSwipeable } from "react-swipeable";
import MapDrawGoogle from "./GoogleMapPolygon";
import MapPolygonViewer from "./GoogleMapPolygonView";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CreateLand from "../api/land/CreateLand";
import DeleteLand from "../api/land/DeleteLand";
import GetLand from "../api/land/GetLand";

const LAND_TYPE_OPTIONS = [
  "แหล่งน้ำที่ใช้รดพืช",
  "พื้นที่แปลงปลูก",
  "บ้านที่อยู่อาศัย",
  "ที่พักผลผลิต",
  "ที่เก็บสารเคมี",
  "อื่นๆ",
];

function convertToRows(data) {
  return data.map((item, index) => {
    const avgLat = item.lat.reduce((sum, val) => sum + val, 0) / item.lat.length;
    const avgLon = item.lon.reduce((sum, val) => sum + val, 0) / item.lon.length;
    return {
      id: index + 1,
      type: item.landType,
      number: item.appLandId,
      lat: avgLat.toFixed(6),
      long: avgLon.toFixed(6),
      rai: item.noOfRais,
      ngan: item.noOfNgan,
      wa: item.noOfWah,
      appLandId: item.appLandId,
    };
  });
}

function convertToPolygons(data) {
  return data.map((item) => {
    const coordinates = item.lat.map((latVal, i) => ({
      lat: latVal,
      lng: item.lon[i],
    }));
    return {
      areaType: item.landType,
      coordinates,
    };
  });
}

function LandManage() {
  const theme = useTheme();
  const userData = JSON.parse(localStorage.getItem("userdata"));
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [create, setCreate] = useState(false);
  const [polygonData, setPolygonData] = useState(null);
  const [rows, setRows] = useState([]);
  const [landPolygons, setLandPolygons] = useState([]);
  const [open, setOpen] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [targetFarmId, setTargetFarmId] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [formData, setFormData] = useState({
    landType: "",
    area: "",
    point: [],
  });

  const handleDialogOpen = () => setOpen(true);
  const handleDialogClose = () => setOpen(false);
  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setOpenSnackbar(false);
  };

  const handleMapFinish = (data) => {
    setPolygonData(data);
    const convertedPoints = data.points.map((p) => ({ lat: p.lat, long: p.lng }));
    setFormData((prev) => ({
      ...prev,
      area: data.area,
      point: convertedPoints,
    }));
    if (data.confirm) handleDialogOpen();
  };

  const fetchLandData = useCallback(async () => {
    if (userData?.registor === 1) {
      try {
        const response = await GetLand({ appFarmerId: userData.appFarmerId });
        setRows(convertToRows(response));
        setLandPolygons(convertToPolygons(response));
        setCurrentIndex(0);
      } catch (error) {
        console.error("Error fetching farmer data:", error);
      }
    }
  }, [userData]);

  useEffect(() => {
    fetchLandData();
  }, []);

  const handleSubmitCreateLand = async () => {
    if (!formData.landType) {
      setSnackbarSeverity("error");
      setSnackbarMessage("กรุณาเลือกประเภทที่ดินก่อนสร้าง");
      setOpenSnackbar(true);
      return;
    }
    try {
      const result = await CreateLand(formData);
      if (result.status === 200) {
        setFormData({ landType: "", area: "", point: [] });
        setSnackbarSeverity("success");
        setSnackbarMessage("สร้างฟาร์มสำเร็จ");
        setOpenSnackbar(true);
        setTimeout(() => {
          setCreate(false);
          handleDialogClose();
          fetchLandData();
        }, 1000);
      } else throw new Error();
    } catch {
      setSnackbarSeverity("error");
      setSnackbarMessage("เกิดข้อผิดพลาด");
      setOpenSnackbar(true);
    }
  };

  const handleDeleteRow = async (appLandId) => {
    try {
      await DeleteLand({ appLandId });
      await fetchLandData();
      setSnackbarSeverity("success");
      setSnackbarMessage("ลบฟาร์มสำเร็จ");
      setOpenSnackbar(true);
    } catch {
      setSnackbarSeverity("error");
      setSnackbarMessage("เกิดข้อผิดพลาดในการลบ");
      setOpenSnackbar(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (targetFarmId) {
      await handleDeleteRow(targetFarmId);
      setConfirmDeleteOpen(false);
      setTargetFarmId(null);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwipe = useSwipeable({
    onSwipedLeft: () => setCurrentIndex((prev) => (prev + 1) % rows.length),
    onSwipedRight: () => setCurrentIndex((prev) => (prev - 1 + rows.length) % rows.length),
    trackMouse: true,
  });

  const columns = [
    { field: "type", headerName: "ประเภทที่ดิน", flex: 1 },
    { field: "number", headerName: "เลขที่ดิน", flex: 1 },
    { field: "lat", headerName: "ละติจูด", flex: 1 },
    { field: "long", headerName: "ลองติจูด", flex: 1 },
    { field: "rai", headerName: "ไร่", type: "number", flex: 0.5 },
    { field: "ngan", headerName: "งาน", type: "number", flex: 0.5 },
    { field: "wa", headerName: "วา", type: "number", flex: 0.5 },
    {
      field: "actions",
      headerName: "ลบ",
      flex: 0.5,
      sortable: false,
      renderCell: (params) => (
        <DeleteIcon
          style={{ cursor: "pointer", color: "red" }}
          onClick={() => {
            setTargetFarmId(params.row.appLandId);
            setConfirmDeleteOpen(true);
          }}
        />
      ),
    },
  ];

  return (
    <>
      <GlobalStyles styles={{ html: { margin: 0, padding: 0, overflow: "hidden", height: "100%" }, body: { margin: 0, padding: 0, overflow: "hidden", height: "100%" } }} />
      {create ? (
        <Box height={isMobile ? "86vh" : "91vh"} overflow={isMobile ? "hidden" : "none"}>
          <Box sx={{ width: "100%", p: 2, backgroundColor: colors.greenAccent[400] }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8} lg={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ display: "none" }}>ประเภทที่ดิน</InputLabel>
                  <Select
                    name="landType"
                    value={formData.landType}
                    onChange={handleChange}
                    sx={{ backgroundColor: "#fff" }}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>กรุณาเลือกประเภทที่ดิน</MenuItem>
                    {LAND_TYPE_OPTIONS.map((type) => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
          <Box height={isMobile ? "75%" : "85%"}>
            <MapDrawGoogle data={handleMapFinish} />
          </Box>
        </Box>
      ) : (
        <Box>
          <Box height={isMobile ? "86vh" : "91vh"} overflow={isMobile ? "hidden" : "none"}>
            <Box height="45%" borderRadius="10px" m="10px">
              <MapPolygonViewer polygons={landPolygons} />
            </Box>
            <Box height="50%" p={1}>
              <Paper elevation={3} sx={{ height: "100%", p: 2 }}>
                <Typography variant="h6" gutterBottom>รายการแปลงที่ดิน</Typography>
                {!isMobile ? (
                  <Box sx={{ height: "calc(100% - 40px)" }}>
                    <DataGrid
                      rows={rows}
                      columns={columns}
                      pageSize={5}
                      rowsPerPageOptions={[5]}
                      disableRowSelectionOnClick
                      sx={{
                        border: "none",
                        "& .MuiDataGrid-columnHeaders": {
                          backgroundColor: colors.greenAccent[400],
                          color: "#000",
                          fontWeight: "bold",
                        },
                        "& .MuiDataGrid-row:hover": {
                          backgroundColor: colors.greenAccent[400],
                        },
                      }}
                    />
                  </Box>
                ) : (
                  <Box {...handleSwipe} sx={{ mt: "-50px", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {rows.length > 0 && rows[currentIndex] ? (
                      <Card sx={{ width: "100%", maxWidth: 300 }}>
                        <CardContent>
                          <Typography variant="subtitle1" fontWeight="bold">ประเภท: {rows[currentIndex].type}</Typography>
                          <Typography>เลขที่ดิน: {rows[currentIndex].number}</Typography>
                          <Typography>ละติจูด: {rows[currentIndex].lat}</Typography>
                          <Typography>ลองติจูด: {rows[currentIndex].long}</Typography>
                          <Typography>
                            ขนาด: {rows[currentIndex].rai} ไร่ {rows[currentIndex].ngan} งาน {rows[currentIndex].wa} วา
                          </Typography>
                          <Box mt={2} display="flex" justifyContent="center">
                            <IconButton
                              onClick={() => {
                                setTargetFarmId(rows[currentIndex].appLandId);
                                setConfirmDeleteOpen(true);
                              }}
                              sx={{
                                color: "red",
                                border: "1px solid red",
                                borderRadius: 2,
                                px: 2,
                                py: 0.5,
                              }}
                            >

                              <DeleteIcon />
                              <Typography sx={{ ml: 1 }}>ลบ</Typography>
                            </IconButton>
                          </Box>
                          <Typography variant="caption" color="textSecondary" display="block" mt={2} align="center">
                            {currentIndex + 1} / {rows.length}
                          </Typography>
                        </CardContent>
                      </Card>
                    ) : (
                      <Typography variant="body2" color="textSecondary">ไม่มีข้อมูลแปลงที่ดิน</Typography>
                    )}
                  </Box>
                )}
              </Paper>
            </Box>
          </Box>
          <Fab
            color="primary"
            aria-label="add"
            sx={{ position: "fixed", bottom: 16, right: 16, zIndex: 1000, backgroundColor: colors.greenAccent[500], "&:hover": { backgroundColor: colors.greenAccent[600] } }}
            onClick={() => setCreate(true)}
          >
            <AddIcon />
          </Fab>
        </Box>
      )}

      <Dialog onClose={handleDialogClose} open={open}>
        <DialogTitle>ยืนยันการสร้างแปลง</DialogTitle>
        <Box p={3}>
          <Box display="flex" justifyContent="space-around">
            <Typography>ประเภท : </Typography>
            <Typography>{formData.landType}</Typography>
          </Box>
          <Box display="flex" justifyContent="space-around">
            <Typography>พื้นที่ : </Typography>
            <Typography>{formData.area}</Typography>
          </Box>
        </Box>
        <Box display="flex" justifyContent="space-around">
          <Button onClick={handleDialogClose}>ยกเลิก</Button>
          <Button onClick={handleSubmitCreateLand}>บันทึก</Button>
        </Box>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={1000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "center", horizontal: "center" }}
        sx={{ "& .MuiSnackbarContent-root": { justifyContent: "center", minWidth: "200px", textAlign: "center" } }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: "100%" }} variant="filled">
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>ยืนยันการลบฟาร์ม</DialogTitle>
        <Box p={2}>
          <Typography>คุณแน่ใจหรือไม่ว่าต้องการลบที่ดินนี้?</Typography>
        </Box>
        <Box display="flex" justifyContent="space-around" pb={2}>
          <Button variant="outlined" onClick={() => { setConfirmDeleteOpen(false); setTargetFarmId(null); }}>
            ยกเลิก
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>
            ลบ
          </Button>
        </Box>
      </Dialog>
    </>
  );
}

export default LandManage;
